import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BrandButton } from "../button";
import {
  Box,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import {
  acme,
  acmePlanCardSx,
  acmePlanBadgeSx,
  acmeMarketingPageSx,
  acmeMarketingHeadlineSx,
  acmeMarketingSectionTitleSx,
  acmeMarketingBodyLeadSx,
} from "../../theme/acmeTheme";
import { Card, CardContent, Table, Tabs, Tab, acmeTabsSx } from "../ui";
import SEOHead from "../SEOHead";
import PriceWithTax from "../PriceWithTax";
import usePricingPlans from "../../hooks/usePricingPlans";
import SubscriptionSavings from "../SubscriptionSavings";
import { pricingMarketingServices } from "./pricingServicesData";
import { usePricingPageUser } from "./usePricingPageUser";
import { isAdminOrManagerRole } from "../../utils/userRoles";
import { FEATURES } from "../../config/features";
import PricingSms from "./PricingSms";

function Pricing() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const { user, currentPlan, totalSpent } = usePricingPageUser();
  const {
    plans: availablePlans,
    loading: plansLoading,
    error: plansError,
  } = usePricingPlans(user, totalSpent);

  const handlePurchasePlan = async (plan) => {
    if (!user) {
      navigate("/register");
      return;
    }

    try {
      if (
        !user.billingAddress ||
        !user.billingCity ||
        !user.billingPostalCode ||
        !user.billingCountry
      ) {
        alert(
          "Please complete your billing information (address, city, postal code, and country) before proceeding with payment.",
        );
        navigate("/billing");
        return;
      }

      navigate("/billing", {
        state: {
          selectedPlan: plan,
          activeTab: 1,
        },
      });
    } catch (error) {
      console.error("Purchase plan error:", error);
      alert("Failed to initiate payment. Please try again later.");
    }
  };

  const handleOrderService = (serviceName) => {
    if (!FEATURES.PROJECTS_ENABLED) {
      navigate("/billing");
      return;
    }

    navigate("/requests", {
      state: {
        selectedService: serviceName,
        autoOpenAddProject: true,
      },
    });
  };

  return (
    <>
      <SEOHead pageName="pricing" />
      <Box sx={{ ...acmeMarketingPageSx, maxWidth: 1400, mx: "auto", px: 2, py: 4 }}>
        <Box sx={{ textAlign: "center", mb: 1, pb: 2 }}>
          <Typography
            component="h1"
            sx={{
              ...acmeMarketingHeadlineSx,
              mb: 2,
              fontSize: { xs: "2rem", sm: "2.5rem", md: "2.75rem" },
            }}
          >
            Pricing
          </Typography>
          <Typography
            sx={{
              ...acmeMarketingBodyLeadSx,
              color: acme.colors.mutedForegroundSolid,
              mx: "auto",
              maxWidth: 600,
              fontSize: { xs: "1rem", sm: "1.125rem" },
            }}
          >
            Discover our competitive pricing options for email marketing and SMS
            campaigns.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={acmeTabsSx}>
            <Tab label="Email Marketing" />
            <Tab label="SMS Marketing" />
          </Tabs>
        </Box>

        {activeTab === 1 ? (
          <PricingSms user={user} />
        ) : (
          <>
            <Card sx={{ p: 4, my: 4, mx: "auto", maxWidth: 1400, boxShadow: "none" }}>
              <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <Typography
                component="h2"
                gutterBottom
                textAlign="center"
                sx={{ ...acmeMarketingSectionTitleSx, mb: 3, fontSize: "1.25rem" }}
              >
                Email Marketing Plan
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 3,
                  justifyContent: "center",
                  mt: 3,
                }}
              >
                {plansLoading && (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <Typography>Loading plans...</Typography>
                  </Box>
                )}

                {plansError && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      p: 3,
                      color: "warning.main",
                    }}
                  >
                    <Typography variant="caption">
                      ⚠️ {plansError} - showing fallback prices
                    </Typography>
                  </Box>
                )}

                {!plansLoading &&
                  availablePlans.map((plan) => {
                    const isPaidPlan = plan.name !== "Basic";
                    const hasOneTimePrice =
                      typeof plan.oneTimePrice === "number" &&
                      plan.oneTimePrice > 0;
                    const hasSubscriptionPrice =
                      typeof plan.subscriptionPrice === "number" &&
                      plan.subscriptionPrice > 0;
                    const pricesUnavailable =
                      isPaidPlan && !hasOneTimePrice && !hasSubscriptionPrice;

                    const isCurrentPlan = user && currentPlan.name === plan.name;
                    const isDisabled = user && isAdminOrManagerRole(user?.role);

                    return (
                      <Card
                        key={plan.name}
                        sx={{
                          ...acmePlanCardSx(isCurrentPlan, isDisabled),
                          width: 280,
                          p: 3,
                        }}
                      >
                        {isCurrentPlan && (
                          <Chip
                            label="Your Plan"
                            sx={acmePlanBadgeSx}
                          />
                        )}
                        <Typography
                          variant="h6"
                          sx={{ mb: 2, fontWeight: "bold" }}
                        >
                          {plan.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mb: 3, color: "text.secondary" }}
                        >
                          Email distribution up to{" "}
                          {plan.limit === Infinity
                            ? "unlimited"
                            : plan.limit.toLocaleString()}{" "}
                          emails per month
                        </Typography>

                        {plan.name === "Basic" ? (
                          <Box sx={{ my: 2 }}>
                            <Typography variant="h5">Free</Typography>
                          </Box>
                        ) : pricesUnavailable ? (
                          <Box sx={{ my: 2 }}>
                            <Typography variant="body2" color="warning.main">
                              Price temporarily unavailable
                            </Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              my: 2,
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {hasOneTimePrice && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  One-time payment
                                </Typography>
                                <PriceWithTax
                                  basePrice={plan.oneTimePrice}
                                  user={user}
                                  showBreakdown={true}
                                  variant="compact"
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  for 30 days access
                                </Typography>
                              </Box>
                            )}

                            {hasSubscriptionPrice && (
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  Monthly subscription
                                </Typography>
                                <PriceWithTax
                                  basePrice={plan.subscriptionPrice}
                                  user={user}
                                  showBreakdown={true}
                                  variant="compact"
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "text.secondary",
                                    display: "block",
                                  }}
                                >
                                  per month, auto-renew
                                </Typography>
                                {hasOneTimePrice &&
                                  plan.oneTimePrice >
                                    plan.subscriptionPrice && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <SubscriptionSavings
                                        oneTimePrice={plan.oneTimePrice}
                                        subscriptionPrice={
                                          plan.subscriptionPrice
                                        }
                                        variant="chip"
                                        color="success"
                                      />
                                    </Box>
                                  )}
                              </Box>
                            )}
                          </Box>
                        )}

                        <Box sx={{ mt: "auto" }}>
                          <BrandButton
                            tone="accent"
                            variant="contained"
                            sx={{
                              width: "100%",
                              py: 1.5,
                              fontWeight: "bold",
                              borderRadius: 2,
                            }}
                            onClick={() => handlePurchasePlan(plan)}
                            disabled={
                              Boolean(
                                user &&
                                (currentPlan.name === plan.name ||
                                  isAdminOrManagerRole(user.role)),
                              ) || pricesUnavailable
                            }
                            title={
                              pricesUnavailable
                                ? "Pricing is temporarily unavailable. Please refresh the page."
                                : undefined
                            }
                          >
                            {user ? "Upgrade" : "Register & Buy"}
                          </BrandButton>
                        </Box>
                      </Card>
                    );
                  })}
              </Box>
              </CardContent>
            </Card>

            <Box sx={{ mt: 6, mb: 3 }}>
              <Typography
                component="h2"
                sx={{
                  ...acmeMarketingSectionTitleSx,
                  textAlign: "center",
                  color: acme.colors.foreground,
                  mb: 2,
                  fontSize: { xs: "1.5rem", md: "1.75rem" },
                }}
              >
                Marketing Services with Proven Results
              </Typography>
              <Typography
                sx={{
                  ...acmeMarketingBodyLeadSx,
                  textAlign: "center",
                  color: acme.colors.mutedForegroundSolid,
                  mb: 4,
                  fontSize: "1rem",
                }}
              >
                See exactly what you'll get - concrete numbers, real ROI,
                measurable growth
              </Typography>
            </Box>

            <Table containerSx={{ mt: 0 }}>
                <TableHead sx={{ backgroundColor: acme.colors.muted }}>
                  <TableRow>
                    <TableCell sx={{ borderBottom: `2px solid ${acme.colors.foreground}` }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#2c3e50" }}
                      >
                        Service
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: `2px solid ${acme.colors.foreground}` }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#2c3e50" }}
                      >
                        Scope of Work
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: `2px solid ${acme.colors.foreground}` }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#2c3e50" }}
                      >
                        What You Get
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: `2px solid ${acme.colors.foreground}` }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#2c3e50" }}
                      >
                        Price Range
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: `2px solid ${acme.colors.foreground}` }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#2c3e50" }}
                      >
                        Action
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pricingMarketingServices.map((service, index) => (
                    <TableRow
                      key={service.name}
                      sx={{
                        backgroundColor:
                          index % 2 === 0 ? "#ffffff" : "#f8f9fa",
                        "&:hover": { backgroundColor: "#e8f5e8" },
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      <TableCell
                        sx={{
                          verticalAlign: "top",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: "bold",
                            color: "#2c3e50",
                            fontSize: "1.1rem",
                          }}
                        >
                          {service.name}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {service.tasks.map((task) => (
                            <li key={task}>
                              <Typography
                                variant="body2"
                                sx={{ color: "#555", mb: 0.5 }}
                              >
                                {task}
                              </Typography>
                            </li>
                          ))}
                        </ul>
                      </TableCell>

                      <TableCell sx={{ borderBottom: "1px solid #e0e0e0" }}>
                        <ul style={{ margin: 0, paddingLeft: "20px" }}>
                          {service.deliverables.map((deliverable) => (
                            <li key={deliverable}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#2c3e50",
                                  fontWeight: 500,
                                  mb: 0.5,
                                  fontSize: "0.9rem",
                                  lineHeight: 1.4,
                                  "& .highlight": {
                                    color: acme.colors.foreground,
                                    fontWeight: "bold",
                                  },
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: deliverable.replace(
                                    /(✅\s*)(\d+[%-]?x?)/g,
                                    '<span class="highlight">$1$2</span>',
                                  ),
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      </TableCell>

                      <TableCell
                        sx={{
                          verticalAlign: "top",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", color: acme.colors.foreground, mb: 1 }}
                        >
                          {service.price}
                        </Typography>
                        <Chip
                          label="per project / monthly"
                          size="small"
                          sx={{
                            backgroundColor: "#e8f5e8",
                            color: "#2c3e50",
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          verticalAlign: "top",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <BrandButton
                          tone="accent"
                          variant="contained"
                          size="small"
                          sx={{
                            fontWeight: "bold",
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                          }}
                          onClick={() => handleOrderService(service.name)}
                        >
                          Order Now
                        </BrandButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
            </Table>
          </>
        )}
      </Box>
    </>
  );
}

export default Pricing;
